import { WeappsWordmark } from '@weapps/design'
import { useLang } from '../i18n'

// Public privacy policy (#239, ISSUE-2026-07-20-239). Reachable without auth at accounts.polux/privacy —
// this URL is declared in Google Play Data Safety and App Store App Privacy. Because grow-mobile is a
// remote shell (ADR 0090) loading the whole app.polux platform, the policy is written at the PLATFORM
// level, not per-app. Copy is self-contained here (not in the shared i18n Strings) so the long-form legal
// text doesn't bloat the type; pt is primary (Brazilian audience, LGPD), en is provided, de falls back to en.
//
// DRAFT pending owner legal sign-off — the data practices below must stay consistent with the Data Safety
// and App Privacy declarations. Update LAST_UPDATED on publication.
const LAST_UPDATED = '2026-08-27'
const CONTACT_EMAIL = 'contato@weapps.net'

type Section = { h: string; p: string[] }
type Doc = { title: string; updated: string; intro: string; sections: Section[] }

const PT: Doc = {
  title: 'Política de Privacidade',
  updated: 'Última atualização',
  intro:
    'Esta Política descreve como a plataforma WeApps trata os dados pessoais de quem usa nossos aplicativos e serviços, incluindo o aplicativo Grow. Ela vale para toda a plataforma, não apenas para um app específico.',
  sections: [
    { h: 'Quem somos', p: ['A WeApps opera a plataforma que hospeda comunidades e conteúdos de aprendizado de organizações (tenants). Cada organização é responsável pelo conteúdo que publica; a WeApps é responsável pela plataforma.'] },
    { h: 'Dados que coletamos', p: [
      'Dados de conta e perfil: seu nome, e-mail e foto de perfil (avatar), quando você os fornece.',
      'Conteúdo que você cria: mensagens e conversas dentro dos grupos e produtos, além do seu progresso de estudo.',
      'Dados técnicos mínimos necessários para autenticar e manter sua sessão.',
    ] },
    { h: 'O que NÃO coletamos', p: ['Não usamos SDKs de publicidade, rastreamento ou analytics de terceiros. Não rastreamos você entre aplicativos ou sites. Não vendemos seus dados.'] },
    { h: 'Como usamos os dados', p: ['Para fornecer o serviço: autenticar você, exibir suas comunidades, grupos e cursos, entregar mensagens e registrar seu progresso. Usamos os dados apenas para o funcionamento do aplicativo e a gestão da sua conta.'] },
    { h: 'Compartilhamento', p: ['Não compartilhamos seus dados pessoais com terceiros para fins de marketing. Dados são visíveis dentro da sua organização e grupos conforme o funcionamento normal da comunidade. Provedores de infraestrutura processam dados sob nossas instruções.'] },
    { h: 'Pagamentos', p: ['Compras de produtos pagos são processadas na web por um provedor de pagamento; os dados de cartão nunca passam pelo aplicativo. A liquidação é feita para a organização vendedora.'] },
    { h: 'Retenção e exclusão', p: [
      'Mantemos seus dados enquanto sua conta existir. Você pode excluir sua conta a qualquer momento pelo aplicativo ou pela web, em accounts.polux.weapps.net/delete-account.',
      'A exclusão vale para toda a plataforma (todas as organizações) e desativa seu acesso. Após um período de carência, os dados são apagados — exceto registros financeiros que a lei exige manter, que são mantidos de forma anonimizada.',
    ] },
    { h: 'Seus direitos (LGPD/GDPR)', p: ['Você pode acessar, corrigir, exportar ou excluir seus dados, e retirar consentimento. Para exercer esses direitos, use os controles no aplicativo ou entre em contato conosco.'] },
    { h: 'Segurança', p: ['Os dados são transmitidos de forma criptografada (HTTPS) e protegidos por controles de acesso.'] },
    { h: 'Crianças', p: ['A plataforma destina-se a públicos adulto e adolescente (13+). Não é direcionada a crianças menores de 13 anos.'] },
    { h: 'Alterações', p: ['Podemos atualizar esta Política. Mudanças relevantes serão comunicadas pelos canais do aplicativo.'] },
    { h: 'Contato', p: [`Dúvidas sobre privacidade: ${CONTACT_EMAIL}.`] },
  ],
}

const EN: Doc = {
  title: 'Privacy Policy',
  updated: 'Last updated',
  intro:
    'This Policy describes how the WeApps platform handles the personal data of people who use our apps and services, including the Grow app. It applies to the whole platform, not just one specific app.',
  sections: [
    { h: 'Who we are', p: ['WeApps operates the platform that hosts organizations’ (tenants’) communities and learning content. Each organization is responsible for the content it publishes; WeApps is responsible for the platform.'] },
    { h: 'Data we collect', p: [
      'Account and profile data: your name, email, and profile picture (avatar) when you provide them.',
      'Content you create: messages and conversations inside groups and products, plus your study progress.',
      'Minimal technical data needed to authenticate and keep your session.',
    ] },
    { h: 'What we do NOT collect', p: ['We use no third-party advertising, tracking, or analytics SDKs. We do not track you across apps or websites. We do not sell your data.'] },
    { h: 'How we use data', p: ['To provide the service: authenticate you, show your communities, groups, and courses, deliver messages, and record your progress. We use data only for app functionality and managing your account.'] },
    { h: 'Sharing', p: ['We do not share your personal data with third parties for marketing. Data is visible within your organization and groups as part of normal community functioning. Infrastructure providers process data under our instructions.'] },
    { h: 'Payments', p: ['Purchases of paid products are processed on the web by a payment provider; card data never passes through the app. Settlement is made to the selling organization.'] },
    { h: 'Retention and deletion', p: [
      'We keep your data while your account exists. You can delete your account at any time in the app or on the web at accounts.polux.weapps.net/delete-account.',
      'Deletion is platform-wide (all organizations) and disables your access. After a grace period, data is erased — except financial records the law requires us to keep, which are retained in de-identified form.',
    ] },
    { h: 'Your rights (LGPD/GDPR)', p: ['You can access, correct, export, or delete your data, and withdraw consent. To exercise these rights, use the in-app controls or contact us.'] },
    { h: 'Security', p: ['Data is transmitted encrypted (HTTPS) and protected by access controls.'] },
    { h: 'Children', p: ['The platform is intended for adult and teen audiences (13+). It is not directed to children under 13.'] },
    { h: 'Changes', p: ['We may update this Policy. Material changes will be communicated through the app.'] },
    { h: 'Contact', p: [`Privacy questions: ${CONTACT_EMAIL}.`] },
  ],
}

export function PrivacyPage() {
  const { lang } = useLang()
  const doc = lang === 'pt' ? PT : EN // de falls back to en

  return (
    <div className="min-h-screen px-6 py-12" style={{ background: 'var(--scr)' }}>
      <div className="mx-auto w-full max-w-2xl">
        <WeappsWordmark className="mb-8 h-7 w-auto" style={{ color: 'var(--txt)' }} />
        <h1 className="t-title-xl" style={{ color: 'var(--txt)' }}>{doc.title}</h1>
        <p className="t-caption mt-1" style={{ color: 'var(--txt3)' }}>{doc.updated}: {LAST_UPDATED}</p>
        <p className="t-body mt-5" style={{ color: 'var(--txt2)' }}>{doc.intro}</p>

        <div className="mt-8 flex flex-col gap-7">
          {doc.sections.map((s) => (
            <section key={s.h}>
              <h2 className="t-title-sm" style={{ color: 'var(--txt)' }}>{s.h}</h2>
              <div className="mt-2 flex flex-col gap-2">
                {s.p.map((para, i) => (
                  <p key={i} className="t-body" style={{ color: 'var(--txt2)' }}>{para}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  )
}
